"use client";

import { useLayoutEffect, useMemo, useRef, useState } from "react";
import { useSession } from "next-auth/react";

import { Button } from "~/app/_components/Button";
import { FullscreenImage } from "./FullscreenImage";
import { maxQuantityForCartLine, useCart } from "~/app/_components/CartContext";
import type { Product } from "~/data/products";
import { formatAttributeName } from "~/data/products";
import { pickBusinessIdForCartLine } from "~/network/customer-products";

function parsePrice(s: string): number {
  const n = parseFloat(s.replace(/[^0-9.]/g, ""));
  return Number.isNaN(n) ? 0 : n;
}

function capitalizeFirstLetter(text: string): string {
  if (!text) return text;
  return text.charAt(0).toUpperCase() + text.slice(1);
}

const OPTION_GAP_PX = 12;

export function ProductAttributesSection({ product }: { product: Product }) {
  const attributes = product.attributes ?? [];
  const { status } = useSession();
  const { addItem, addItemAndSyncToServer, openCart, items } = useCart();
  const listRef = useRef<HTMLUListElement>(null);
  const [scrollMaxHeight, setScrollMaxHeight] = useState<number>();
  const [addingAttributeId, setAddingAttributeId] = useState<number | null>(null);
  const shouldScroll = attributes.length > 3;

  useLayoutEffect(() => {
    if (!shouldScroll) {
      setScrollMaxHeight(undefined);
      return;
    }

    const list = listRef.current;
    if (!list) return;

    const measure = () => {
      const optionItems = Array.from(list.children) as HTMLElement[];
      if (optionItems.length < 3) return;

      const height = optionItems.slice(0, 3).reduce((total, item, index) => {
        return total + item.offsetHeight + (index < 2 ? OPTION_GAP_PX : 0);
      }, 0);

      setScrollMaxHeight(height);
    };

    measure();
    const observer = new ResizeObserver(measure);
    for (const item of list.children) observer.observe(item);
    return () => observer.disconnect();
  }, [shouldScroll, attributes]);

  const qtyByAttributeId = useMemo(() => {
    const map = new Map<number, number>();
    for (const i of items) {
      if (i.productId !== product.id || !i.attribute) continue;
      const prev = map.get(i.attribute.id) ?? 0;
      map.set(i.attribute.id, prev + i.quantity);
    }
    return map;
  }, [items, product.id]);

  if (!attributes.length) return null;

  async function addAttributeToCart(
    attr: NonNullable<Product["attributes"]>[number],
  ) {
    if (addingAttributeId !== null) return;

    const unitBasePrice =
      attr.unitPrice ?? Math.round(parsePrice(product.price));
    const businessId = pickBusinessIdForCartLine(product, attr);
    const image = attr.images?.[0] ?? product.image;
    const variantLabel = `${formatAttributeName(attr.name)}: ${attr.value}`;
    const variantMax = maxQuantityForCartLine(attr.quantity);
    const attributeImage = attr.images?.[0];
    const line = {
      productId: product.id,
      name: `${product.name} (${variantLabel})`,
      price: `$${unitBasePrice.toFixed(2)}`,
      image,
      unitBasePrice,
      attribute: {
        id: attr.id,
        price: unitBasePrice,
        name: attr.name,
        value: attr.value,
        ...(attributeImage ? { image: attributeImage } : {}),
      },
      ...(businessId ? { businessId } : {}),
      ...(variantMax != null ? { maxQuantity: variantMax } : {}),
    };
    if (status === "authenticated") {
      setAddingAttributeId(attr.id);
      try {
        await addItemAndSyncToServer(line, 1);
      } finally {
        setAddingAttributeId(null);
      }
    } else {
      addItem(line, 1);
    }
    openCart();
  }

  return (
    <section className="mt-6 space-y-3 border-t border-stone-200 pt-6">
      <h2 className="text-sm font-semibold text-stone-900">Options</h2>
      <ul
        ref={listRef}
        className={`space-y-3${shouldScroll ? " overflow-y-auto pr-1" : ""}`}
        style={scrollMaxHeight != null ? { maxHeight: scrollMaxHeight } : undefined}
      >
        {attributes.map((attr) => {
          const inCart = qtyByAttributeId.get(attr.id) ?? 0;
          const maxQ = maxQuantityForCartLine(attr.quantity);
          const outOfStock = maxQ === 0;
          const atMax = maxQ != null && inCart >= maxQ;
          const hasImages = Boolean(attr.images?.length);

          return (
            <li
              key={attr.id}
              className="rounded-lg border border-stone-200 bg-white p-4 shadow-sm"
            >
              <div
                className={
                  hasImages
                    ? "flex flex-col gap-4 sm:grid sm:grid-cols-[1fr_auto] sm:items-start sm:gap-5"
                    : "flex flex-col"
                }
              >
                <div className="min-w-0 flex flex-col">
                  <div className="text-xs font-medium uppercase tracking-wide text-stone-500">
                    {formatAttributeName(attr.name)}
                  </div>
                  <p className="mt-1 text-base font-medium text-stone-900">
                    {capitalizeFirstLetter(attr.value)}
                  </p>
                  {attr.notes ? (
                    <p className="mt-1 text-sm text-stone-600">{attr.notes}</p>
                  ) : null}
                  <div className="mt-2 flex flex-wrap items-center gap-3 text-sm text-stone-600">
                    {attr.priceLabel ? (
                      <span className="font-semibold text-amber-700">
                        {attr.priceLabel}
                      </span>
                    ) : null}
                    {attr.quantity != null ? (
                      <span>{attr.quantity} in stock</span>
                    ) : null}
                  </div>
                  <div className="mt-4 flex justify-start">
                    <Button
                      type="button"
                      loading={addingAttributeId === attr.id}
                      disabled={outOfStock || atMax}
                      onClick={() => void addAttributeToCart(attr)}
                      className="rounded-lg px-4 py-2 text-sm disabled:cursor-not-allowed"
                    >
                      {outOfStock
                        ? "Out of stock"
                        : atMax
                          ? "Max in cart"
                          : "Add to Cart"}
                    </Button>
                  </div>
                </div>
                {hasImages && attr.images ? (
                  <div className="flex shrink-0 flex-wrap content-start justify-start gap-2 sm:justify-end">
                    {attr.images.map((src) => (
                      <FullscreenImage
                        key={src}
                        src={src}
                        alt={`${product.name} — ${formatAttributeName(attr.name)}`}
                        className="h-20 w-20 shrink-0 rounded-md bg-stone-100 sm:h-24 sm:w-24"
                        sizes="96px"
                      />
                    ))}
                  </div>
                ) : null}
              </div>
            </li>
          );
        })}
      </ul>
    </section>
  );
}
